import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  listTickets,
  getTicket,
  createTicket,
  replyTicket,
  closeTicket,
} from "../../services/supportService";
import { usePlanFeature } from "../../hooks/usePlanFeature";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import TicketCard from "./components/TicketCard";
import TicketDetail from "./components/TicketDetail";
import TicketFormModal from "./components/TicketFormModal";
import SupportPlanGate from "./components/SupportPlanGate";
import { STATUS_FILTERS } from "./supportConstants";
import styles from "./Support.module.css";

const MOBILE_BREAKPOINT = 768;
const CHAT_POLL_MS = 5000;

export default function Support() {
  // Gate SÓ na criação. A página e a listagem ficam abertas a qualquer tenant.
  const { allowed: canCreate, tier, upgradeUrl } = usePlanFeature("support");

  const [tickets, setTickets] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  const [selectedId, setSelectedId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  // 403 na criação → exibe SupportPlanGate inline (defesa em profundidade,
  // além do pré-check de plano). Nunca toast/modal técnico nesse caso.
  const [gateBlocked, setGateBlocked] = useState(false);

  const [confirmClose, setConfirmClose] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );

  const pollingRef = useRef(null);

  const showGate = !canCreate || gateBlocked;

  // ─── Viewport ──────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ─── Fetch lista ───────────────────────────────────────────────────
  const activeFilter = useMemo(
    () => STATUS_FILTERS.find((f) => f.id === filter) ?? STATUS_FILTERS[0],
    [filter],
  );

  const fetchTickets = useCallback(async () => {
    try {
      setListLoading(true);
      // "Encerrados" agrupa RESOLVED + CLOSED; o backend aceita múltiplos status
      // por vírgula ("CLOSED,RESOLVED") e já devolve ordenado por updatedAt desc.
      const params = activeFilter.query ? { status: activeFilter.query } : {};
      const res = await listTickets(params);
      const data = Array.isArray(res?.data)
        ? res.data
        : res?.data?.tickets ?? [];
      setTickets(data);
    } catch {
      toast.error("Não foi possível carregar seus chamados. Tente novamente.");
    } finally {
      setListLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // ─── Fetch detalhe ─────────────────────────────────────────────────
  const fetchSelected = useCallback(async (id, { silent = false } = {}) => {
    if (!id) return;
    try {
      if (!silent) setDetailLoading(true);
      const res = await getTicket(id);
      setSelectedTicket(res.data);
    } catch {
      if (!silent) {
        toast.error("Não foi possível abrir este chamado.");
      }
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, []);

  const handleSelect = useCallback(
    (ticket) => {
      setSelectedId(ticket.id);
      setSelectedTicket(null);
      setReply("");
      fetchSelected(ticket.id);
    },
    [fetchSelected],
  );

  const handleBack = useCallback(() => {
    setSelectedId(null);
    setSelectedTicket(null);
    setReply("");
  }, []);

  // ─── Polling (tier CHAT) ───────────────────────────────────────────
  useEffect(() => {
    if (selectedId && tier === "CHAT") {
      pollingRef.current = setInterval(
        () => fetchSelected(selectedId, { silent: true }),
        CHAT_POLL_MS,
      );
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [selectedId, tier, fetchSelected]);

  // ─── Responder (otimista) ──────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const content = reply.trim();
    if (!content || !selectedTicket || sending) return;

    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      _tempId: tempId,
      _pending: true,
      content,
      isStaff: false,
      createdAt: new Date().toISOString(),
    };

    setSelectedTicket((prev) =>
      prev ? { ...prev, messages: [...(prev.messages ?? []), optimistic] } : prev,
    );
    setReply("");
    setSending(true);

    try {
      await replyTicket(selectedTicket.id, content);
      // Re-sincroniza com o servidor (status pode ter ido OPEN → IN_PROGRESS).
      await fetchSelected(selectedTicket.id, { silent: true });
      fetchTickets();
    } catch {
      // Remove a mensagem otimista e devolve o texto ao campo.
      setSelectedTicket((prev) =>
        prev
          ? {
              ...prev,
              messages: (prev.messages ?? []).filter((m) => m._tempId !== tempId),
            }
          : prev,
      );
      setReply(content);
      toast.error("Não foi possível enviar sua mensagem. Tente novamente.");
    } finally {
      setSending(false);
    }
  }, [reply, selectedTicket, sending, fetchSelected, fetchTickets]);

  // ─── Encerrar ──────────────────────────────────────────────────────
  const handleConfirmClose = useCallback(async () => {
    if (!selectedTicket) return;
    setConfirmClose(false);
    try {
      await closeTicket(selectedTicket.id);
      toast.success("Chamado encerrado.");
      await fetchSelected(selectedTicket.id, { silent: true });
      fetchTickets();
    } catch {
      toast.error("Não foi possível encerrar o chamado.");
    }
  }, [selectedTicket, fetchSelected, fetchTickets]);

  // ─── Criar ─────────────────────────────────────────────────────────
  const handleOpenCreate = useCallback(() => {
    if (showGate) return; // botão desabilitado quando gate ativo
    setShowCreate(true);
  }, [showGate]);

  const handleCreate = useCallback(
    async (payload) => {
      setCreating(true);
      try {
        const res = await createTicket(payload);
        const created = res.data;
        setShowCreate(false);
        toast.success("Chamado aberto! Nossa equipe responderá em breve.");
        await fetchTickets();
        if (created?.id) {
          setSelectedId(created.id);
          setReply("");
          fetchSelected(created.id);
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          // Gate defensivo: fecha o modal e mostra o aviso inline. Sem toast.
          setShowCreate(false);
          setGateBlocked(true);
        } else {
          toast.error("Não foi possível abrir o chamado. Tente novamente.");
        }
      } finally {
        setCreating(false);
      }
    },
    [fetchTickets, fetchSelected],
  );

  // ─── Render ────────────────────────────────────────────────────────
  const showDetailPanel = !isMobile || selectedId;
  const showListPanel = !isMobile || !selectedId;

  return (
    <div className={styles.container}>
      {showListPanel && (
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h1 className={styles.heading}>Meus chamados</h1>

            <div className={styles.filters} role="tablist" aria-label="Filtrar chamados">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  className={`${styles.chip} ${filter === f.id ? styles.chipActive : ""}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={styles.newBtn}
              onClick={handleOpenCreate}
              disabled={showGate}
              aria-disabled={showGate}
            >
              <span aria-hidden="true">＋</span> Novo chamado
            </button>

            {showGate && <SupportPlanGate upgradeUrl={upgradeUrl} />}
          </div>

          <div className={styles.list}>
            {listLoading ? (
              <ListSkeleton />
            ) : tickets.length === 0 ? (
              <EmptyState canCreate={!showGate} onCreate={handleOpenCreate} />
            ) : (
              tickets.map((t) => (
                <TicketCard
                  key={t.id}
                  ticket={t}
                  selected={selectedId === t.id}
                  onSelect={handleSelect}
                />
              ))
            )}
          </div>
        </aside>
      )}

      {showDetailPanel && (
        <div className={styles.detailPanel}>
          <TicketDetail
            ticket={selectedTicket}
            loading={detailLoading}
            reply={reply}
            onReplyChange={setReply}
            onSend={handleSend}
            sending={sending}
            onClose={() => setConfirmClose(true)}
            onBack={handleBack}
            isMobile={isMobile}
          />
        </div>
      )}

      {showCreate && (
        <TicketFormModal
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
          submitting={creating}
        />
      )}

      <ConfirmDialog
        isOpen={confirmClose}
        title="Encerrar este chamado?"
        message="Isso indica que o problema foi resolvido. Você ainda poderá ver o histórico depois."
        confirmLabel="Encerrar"
        cancelLabel="Cancelar"
        confirmVariant="primary"
        onConfirm={handleConfirmClose}
        onCancel={() => setConfirmClose(false)}
      />
    </div>
  );
}

/* ─── Estado vazio motivacional ──────────────────────────────────── */
function EmptyState({ canCreate, onCreate }) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon} aria-hidden="true">🎧</span>
      <h2 className={styles.emptyTitle}>Você ainda não tem nenhum chamado</h2>
      <p className={styles.emptyText}>
        Tem alguma dúvida ou problema? Abra um chamado e nossa equipe te ajuda em breve.
      </p>
      {canCreate && (
        <button type="button" className={styles.newBtn} onClick={onCreate}>
          <span aria-hidden="true">＋</span> Abrir meu primeiro chamado
        </button>
      )}
    </div>
  );
}

/* ─── Skeleton da lista ──────────────────────────────────────────── */
function ListSkeleton() {
  return (
    <div className={styles.listSkeleton} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={styles.cardSkeleton} />
      ))}
    </div>
  );
}
